import type { IQuery } from "@citrine/client";
import ClientBase from "@citrine/client";
import { queryClient } from "@citrine/client/utils";

import { DesignWorkflowExecutionClient } from "../design-workflow-execution";

import type { DesignWorkflow } from "./design-workflow";

interface DesignWorkflowQuery extends IQuery {
  page?: number;
  per_page?: number;
  branch?: string;
  sort_by?: string;
  filter?: string;
}

export class DesignWorkflowClient extends ClientBase<
  DesignWorkflow,
  string,
  DesignWorkflowQuery
> {
  protected constructor(protected projectId: string) {
    super({
      endpoint: `/api/v2/projects/${projectId}/design-workflows`,
      findField: "response",
      defaultQuery: {
        page: 1,
        // these objects are lightweight, so we can request more than 20 at a time
        per_page: 100,
        branch: undefined,
        sort_by: undefined,
        filter: undefined,
      },
    });
  }

  static getInstance(projectId: string) {
    return new this(projectId);
  }

  async create(model: Partial<DesignWorkflow>): Promise<DesignWorkflow> {
    const workspace = await super.create(model);

    // add empty execution arrays to the cache since we know they can't exist yet
    const cacheKey = DesignWorkflowExecutionClient.getInstance(
      this.projectId,
      workspace.id
    ).getQueryCacheKey();
    queryClient.setQueryData(cacheKey, []);
    queryClient.setQueryData([...cacheKey, "all"], []);

    return workspace;
  }

  async archive(idsToArchive: string): Promise<DesignWorkflow>;
  async archive(idsToArchive: string[]): Promise<DesignWorkflow[]>;
  async archive(
    idsToArchive: string[] | string
  ): Promise<DesignWorkflow[] | DesignWorkflow> {
    const results = await Promise.all(
      ([] as string[]).concat(idsToArchive).map((id) =>
        this.request<DesignWorkflow>({
          method: "PUT",
          url: `${this.endpoint}/${id}/archive`,
        })
      )
    );
    results.forEach((result) => {
      this.invalidateAfterMutation(result.id);
    });
    return typeof idsToArchive === "string" ? results[0] : results;
  }

  protected fromModel(model: Partial<DesignWorkflow>): Partial<DesignWorkflow> {
    // convert any empty strings to null
    return {
      ...model,
      data_source_id: model.data_source_id || null,
      design_space_id: model.design_space_id || null,
      processor_id: model.processor_id || null,
      predictor_id: model.predictor_id || null,
    };
  }
}
